import recordEvent from 'platform/monitoring/record-event';
import { apiRequest } from 'platform/utilities/api';
import { focusElement } from 'platform/utilities/ui';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import content from '../locales/en/content.json';
import { replaceStrValues } from '../utils/formatting';

const DEFAULT_FILE_NAME = 'Form_Submission';

const sanitizeFileName = name =>
  name
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^\p{L}\p{N}_-]/gu, '');

const FormDownloadLink = ({
  apiEndpoint,
  formConfig,
  fullName,
  rootUrl,
  transformer,
}) => {
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const linkRef = useRef(null);
  const errorRef = useRef(null);
  const restoreFocus = useRef(false);

  const form = useSelector(state => state.form);
  const formData = useMemo(
    () => transformer(formConfig, form, true),
    [formConfig, form, transformer],
  );

  const formId = formConfig?.formId;
  const trackingPrefix = formConfig?.trackingPrefix ?? '';

  const fileName = useMemo(() => {
    const name = sanitizeFileName(fullName || '') || DEFAULT_FILE_NAME;
    return `${formId}_${name}.pdf`;
  }, [formId, fullName]);

  const handlePdfDownload = useCallback(
    blob => {
      const downloadUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');

      downloadLink.href = downloadUrl;
      downloadLink.download = fileName;
      downloadLink.className = 'dd-privacy-hidden';
      document.body.appendChild(downloadLink);

      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadUrl);
    },
    [fileName],
  );

  const fetchPdf = useCallback(
    async event => {
      event.preventDefault();
      setLoading(true);
      setHasError(false);
      restoreFocus.current = false;

      try {
        const response = await apiRequest(apiEndpoint, {
          method: 'POST',
          body: formData,
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) throw new Error();

        const blob = await response.blob();
        handlePdfDownload(blob);
        restoreFocus.current = true;
        recordEvent({ event: `${trackingPrefix}pdf-download--success` });
      } catch {
        setHasError(true);
        recordEvent({ event: `${trackingPrefix}pdf-download--failure` });
      } finally {
        setLoading(false);
      }
    },
    [apiEndpoint, formData, handlePdfDownload, trackingPrefix],
  );

  useEffect(() => {
    if (hasError) {
      focusElement(errorRef.current);
    } else if (!loading && restoreFocus.current) {
      restoreFocus.current = false;
      focusElement('a', {}, linkRef.current);
    }
  }, [hasError, loading]);

  if (loading) {
    return (
      <va-loading-indicator
        label={content['form-download--loading-label']}
        message={content['form-download--loading-message']}
        set-focus
        inline
      />
    );
  }

  const [errorMessage, linkText] = [
    content['form-download--error-message'],
    content['form-download--link-text'],
  ].map(v => replaceStrValues(v, formId));

  return (
    <>
      {hasError && (
        <div className="vads-u-margin-y--1">
          <va-alert status="error">
            <span ref={errorRef}>{errorMessage}</span>
          </va-alert>
        </div>
      )}
      <va-link
        ref={linkRef}
        text={linkText}
        onClick={fetchPdf}
        filetype="PDF"
        href={rootUrl}
        download
      />
    </>
  );
};

FormDownloadLink.propTypes = {
  apiEndpoint: PropTypes.string.isRequired,
  formConfig: PropTypes.shape({
    // rendered to the user, so pass a display-formatted id (10-10EZ, not 1010ez)
    formId: PropTypes.string.isRequired,
    trackingPrefix: PropTypes.string,
  }).isRequired,
  rootUrl: PropTypes.string.isRequired,
  transformer: PropTypes.func.isRequired,
  fullName: PropTypes.string,
};

export default FormDownloadLink;
